import { Project } from "@nova/protobuf"

export type SortableEntity = { index: string; parentId?: string }
const CHARSET = "#$0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
export const START = CHARSET[0]
export const END = CHARSET.at(-1)!
const LEN = CHARSET.length
const RANK_MIN = CHARSET[0]
const CHAR_MAP = CHARSET.split("").reduce(
    (acc, cur, i) => {
        acc[cur] = i
        return acc
    },
    {} as Record<string, number>
)

const MIDDLE_CHAR = CHARSET[Math.floor((CHARSET.length - 1) / 2)]
const MIDDLE_FRAC = CHAR_MAP[MIDDLE_CHAR]

const strsToFractions = (startStr: string, endStr: string) => {
    const diff = startStr.length - endStr.length

    // Pad values with "a" so they have equal length
    if (diff > 0) {
        endStr = endStr.padEnd(startStr.length, RANK_MIN)
    } else if (diff < 0) {
        startStr = startStr.padEnd(endStr.length, RANK_MIN)
    }

    // convert strings to number arrays and pad with 0 at the beginning for addition overflow
    const start = startStr.split("").map((char) => CHAR_MAP[char])
    const end = endStr.split("").map((char) => CHAR_MAP[char])

    return [start, end]
}

const fracToStr = (frac: number[]) => {
    return frac.map((digit) => CHARSET[digit]).join("")
}

const sumFractions = (start: number[], end: number[]) => {
    start = [0, ...start]
    end = [0, ...end]

    const result: number[] = new Array(end.length).fill(0)

    // start and end should be equal lengths

    for (let i = end.length - 1; i > 0; i--) {
        const [a, b] = [start[i], end[i]]
        const c = a + b
        result[i] += c
    }

    if (result[0] === 0) result.shift()

    return result
}

const divideFraction = (dividend: number[], divisor = 2) => {
    const quotient = Array(dividend.length).fill(0)
    let remainder = 0

    dividend.forEach((digit, i) => {
        const current = digit + remainder * LEN

        const quotientDigit = Math.floor(current / divisor)
        remainder = current % divisor

        quotient[i] = quotientDigit
    })

    for (let i = quotient.length - 1; i > 0; i--) {
        if (quotient[i] >= LEN) {
            quotient[i - 1] += Math.floor(quotient[i] / LEN)
            quotient[i] %= LEN
        }
    }

    return [quotient, remainder] as [number[], number]
}

export const createFractionalIndexBetween = (index1: string, index2: string) => {
    const [frac1, frac2] = strsToFractions(index1, index2)
    const sum = sumFractions(frac1, frac2)
    const [quotient, remainder] = divideFraction(sum)
    const collapsedOnto = (frac: number[]) => quotient.every((digit, i) => digit === frac[i])
    if (remainder && (collapsedOnto(frac1) || collapsedOnto(frac2))) quotient.push(MIDDLE_FRAC)
    return fracToStr(quotient)
}

export const createNewLayerFractionalIndex = (project: Project, steps = 0, inParentId?: string) => {
    const topLevelIndicies = [
        ...project.infraredFlights.filter((e) => !e.mergeId),
        ...project.orthoFlights,
        ...project.satelliteFlights,
        ...project.videoFlights,
        ...project.videoColorFinderFlights,
        ...project.imageColorFinderFlights,
        ...project.panoramaFlights,
        ...project.icons,
        ...project.lines,
        ...project.texts,
        ...project.perimeters,
        ...project.PDFRasters,
        ...project.groups,
        ...project.liveVideoFlights,
        ...project.mergedFlights,
        ...project.rulers,
    ]
    return createNewFractionalIndex(topLevelIndicies, steps, inParentId)
}

export const createNewPDFFractionalIndex = (project: Project, steps = 0, inParentId?: string) => {
    const topLevelIndicies = [...project.PDFs]
    return createNewFractionalIndex(topLevelIndicies, steps, inParentId)
}

const createNewFractionalIndex = (entities: SortableEntity[], steps = 0, inParentId?: string) => {
    const topLevelIndicies = entities
        .filter(
            (layer) =>
                (!!inParentId
                    ? layer.parentId === inParentId
                    : !layer.parentId || layer.parentId === "null") && layer.index
        ) // prototodo
        .map((layer) => layer.index)

    topLevelIndicies.sort().reverse()
    let topIndex = topLevelIndicies[0]
    for (let i = 0; i < steps + 1; i++) {
        topIndex = createFractionalIndexBetween(topIndex || START, END)
    }
    return topIndex
}

export const compareFractionalIndices = (a: string, b: string) => {
    const len = Math.max(a.length, b.length)
    for (let i = 0; i < len; i++) {
        const [rankA, rankB] = [CHAR_MAP[a[i]], CHAR_MAP[b[i]]]
        if (rankA !== rankB) return (rankA || -1) - (rankB || -1)
    }
    return 0
}
